import {
  isModuleEnabled,
  moduleOption,
  type OrgModulesState,
} from "@/modules/registry";
import { useMemo, useState } from "react";
import { useGetIdentity } from "ra-core";
import { useNavigate } from "react-router";
import {
  Building2,
  CalendarClock,
  MessageSquare,
  Plus,
  UserPlus,
  Briefcase,
} from "lucide-react";

import { IconButton } from "@/components/ui/icon-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  canAccess,
  type AccessIdentity,
} from "@/components/atomic-crm/providers/commons/canAccess";
import {
  hasMemberCapability,
  resolveEffectiveModules,
} from "@/components/atomic-crm/providers/commons/memberModuleAccess";
import { getClientCreatePath, getContactCreatePath } from "@/app/routing";
import { getNewDealManualCreatePath } from "@/modules/deals/projectCreatePaths";
import { BookNowShareDialog } from "@/modules/booking/BookNowShareDialog";

export const GlobalQuickCreateMenu = () => {
  const navigate = useNavigate();
  const { identity } = useGetIdentity();
  const [bookNowOpen, setBookNowOpen] = useState(false);

  const actions = useMemo(() => {
    if (!identity || typeof identity !== "object") return [];
    const access = identity as AccessIdentity;

    const modules = resolveEffectiveModules(access);
    // Company-level modules (Apps) on top of the member's own access.
    const orgModules = (identity as { org_modules?: OrgModulesState | null })
      .org_modules;
    const peopleOnly = moduleOption(orgModules, "accounts", "peopleOnly");
    const items: Array<{
      id: string;
      label: string;
      icon: typeof Plus;
      onSelect: () => void;
      section: "create" | "share";
    }> = [];

    if (canAccess(access, { resource: "contacts", action: "create" })) {
      items.push({
        id: "contact",
        label: "New contact",
        icon: UserPlus,
        section: "create",
        onSelect: () => navigate(getContactCreatePath()),
      });
    }

    if (
      !peopleOnly &&
      canAccess(access, { resource: "companies", action: "create" })
    ) {
      items.push({
        id: "company",
        label: "New account",
        icon: Building2,
        section: "create",
        onSelect: () => navigate(getClientCreatePath()),
      });
    }

    if (
      isModuleEnabled(orgModules, "pipeline") &&
      canAccess(access, { resource: "deals", action: "create" })
    ) {
      items.push({
        id: "deal",
        label: "New project",
        icon: Briefcase,
        section: "create",
        onSelect: () => navigate(getNewDealManualCreatePath()),
      });
    }

    if (modules.messaging && hasMemberCapability(access, "messaging.send")) {
      if (isModuleEnabled(orgModules, "messages")) {
        items.push({
          id: "message",
          label: "New message",
          icon: MessageSquare,
          section: "share",
          onSelect: () => navigate("/messages"),
        });
      }

      items.push({
        id: "book-now",
        label: "Book now",
        icon: CalendarClock,
        section: "share",
        onSelect: () => setBookNowOpen(true),
      });
    }

    return items;
  }, [identity, navigate]);

  if (actions.length === 0) return null;

  const createActions = actions.filter((item) => item.section === "create");
  const shareActions = actions.filter((item) => item.section === "share");

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <IconButton
                variant="ghost"
                className="size-9 shrink-0 data-[state=open]:bg-accent"
                aria-label="Quick create"
              >
                <Plus className="size-4" />
              </IconButton>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Quick create</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-52">
          {createActions.length > 0 ? (
            <>
              <DropdownMenuLabel>Create</DropdownMenuLabel>
              {createActions.map((item) => (
                <DropdownMenuItem
                  key={item.id}
                  onClick={item.onSelect}
                  className="gap-2"
                >
                  <item.icon className="size-4" />
                  {item.label}
                </DropdownMenuItem>
              ))}
            </>
          ) : null}
          {createActions.length > 0 && shareActions.length > 0 ? (
            <DropdownMenuSeparator />
          ) : null}
          {shareActions.length > 0 ? (
            <>
              <DropdownMenuLabel>Share</DropdownMenuLabel>
              {shareActions.map((item) => (
                <DropdownMenuItem
                  key={item.id}
                  onClick={item.onSelect}
                  className="gap-2"
                >
                  <item.icon className="size-4" />
                  {item.label}
                </DropdownMenuItem>
              ))}
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <BookNowShareDialog open={bookNowOpen} onOpenChange={setBookNowOpen} />
    </>
  );
};
